import { Link } from 'react-router-dom';
import { useLang } from '../i18n/context';
import SEO from '../components/SEO';
import { Container, Reveal, SectionHeader } from '../components/Reveal';
import Journey from '../components/Journey';
import { breadcrumbSchema } from '../lib/schema';
import { DOCTORS } from '../data/doctors';

export default function About() {
    const { t, lang } = useLang();
    return (
        <>
            <SEO
                title={t.about.metaTitle}
                description={t.about.metaDesc}
                path="/about"
                lang={lang}
                jsonLd={[breadcrumbSchema(lang, [{ name: t.nav.home, path: '/' }, { name: t.about.title, path: '/about' }])]}
            />
            <section className="relative overflow-hidden pt-28 md:pt-36" data-testid="about-page">
                <div className="pointer-events-none absolute -right-32 -top-20 h-96 w-96 rounded-full bg-peri-200/70" aria-hidden="true" />
                <Container className="relative">
                    <Reveal>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gold-700">{t.about.title}</p>
                        <h1 className="mt-4 max-w-3xl font-serif text-4xl font-semibold leading-tight tracking-tight text-ink md:text-6xl">{t.about.title}</h1>
                        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-muted">{t.about.intro}</p>
                    </Reveal>
                </Container>
            </section>

            <section className="py-16 md:py-24">
                <Container>
                    <SectionHeader eyebrow={t.about.promisesTitle} title={t.about.promisesTitle} />
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {t.about.promises.map((p, i) => (
                            <Reveal key={p.t} delay={i * 0.06} className="h-full">
                                <div className="h-full rounded-2xl border border-peri-200 bg-white p-7">
                                    <span className="font-serif text-3xl italic text-peri-300">{String(i + 1).padStart(2, '0')}</span>
                                    <h3 className="mt-4 font-serif text-xl font-semibold text-ink">{p.t}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-ink-muted">{p.d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </section>

            <section className="bg-peri-50 py-16 md:py-24">
                <Container>
                    <SectionHeader eyebrow={t.home.journey.eyebrow} title={t.about.philosophyTitle} />
                    <Journey />
                </Container>
            </section>

            <section className="py-16 md:py-24">
                <Container>
                    <div className="grid items-start gap-10 lg:grid-cols-2">
                        <Reveal>
                            <h2 className="font-serif text-2xl font-semibold text-ink md:text-3xl">{t.home.doctors.title}</h2>
                            <div className="mt-6 space-y-4">
                                {DOCTORS.map((d) => (
                                    <Link key={d.slug} to={`/dental/${lang}/doctors/${d.slug}`} data-testid={`about-doctor-${d.slug}`} className="block rounded-2xl border border-peri-200 bg-white p-6 transition-all hover:border-gold-500 hover:shadow-card-hover">
                                        <p className="font-serif text-xl font-semibold text-ink">{d.name}</p>
                                        <p className="mt-1 text-sm font-semibold text-gold-700">{d.creds} · {d.key === 'irma' ? t.doctors.irmaRoles : t.doctors.ariandiRoles}</p>
                                    </Link>
                                ))}
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <figure>
                                <img src="/img/clinic-entrance.png" alt="Klinik Pergigian Irma Ariandi — our clinic team at the entrance" loading="lazy" className="aspect-[4/3] w-full rounded-3xl border border-peri-200 object-cover" data-testid="about-entrance-img" />
                            </figure>
                            <figure className="mt-4">
                                <img src="/img/doctors-duo.png" alt="Dr. Irma (DDS, UKM) and Dr. Ariandi (DDS, UKM)" loading="lazy" className="aspect-[4/3] w-full rounded-3xl border border-peri-200 object-cover" data-testid="about-doctors-img" />
                            </figure>
                        </Reveal>
                    </div>
                </Container>
            </section>
        </>
    );
}
